<template>
  <div class="hex-color-field">
    <span v-if="label" class="hex-color-field__label">{{ label }}</span>

    <div class="hex-color-field__row">
      <ColorPicker
        :inputId="inputId"
        v-model="pickerModel"
        format="hex"
        appendTo="body"
        :disabled="disabled"
        class="hex-color-field__picker"
        @change="onPickerChange"
      />

      <FloatLabel variant="on" class="hex-color-field__input">
        <InputText
          :id="`${inputId}-hex`"
          v-model="textDraft"
          :disabled="disabled"
          inputmode="text"
          autocomplete="off"
          maxlength="7"
          class="w-full rounded-md"
          :class="mono ? 'font-mono' : ''"
          :invalid="showError"
          @blur="commitText"
          @keydown.enter.prevent="commitText"
        />
        <label :for="`${inputId}-hex`" class="text-xs">{{ hexLabel }}</label>
      </FloatLabel>
    </div>

    <small v-if="showError" class="hex-color-field__error">
      {{ invalidMessage }}
    </small>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import ColorPicker from 'primevue/colorpicker';
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import {
  addHash,
  isValidHexWithHash,
  normalizeHexWithHash,
  stripHash,
} from '@/utils/hexColor';

defineOptions({ name: 'HexColorField' });

const props = defineProps({
  modelValue: {
    type: String,
    default: '#64748B',
  },
  label: {
    type: String,
    default: '',
  },
  inputId: {
    type: String,
    required: true,
  },
  hexLabel: {
    type: String,
    default: 'HEX',
  },
  fallbackColor: {
    type: String,
    default: '#64748B',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  invalid: {
    type: Boolean,
    default: false,
  },
  invalidMessage: {
    type: String,
    default: 'Invalid HEX (e.g. #AABBCC)',
  },
  mono: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const textDraft = ref('');
const touched = ref(false);

const normalizedColor = computed(() =>
  normalizeHexWithHash(props.modelValue, props.fallbackColor)
);

const showError = computed(
  () => props.invalid && !isValidHexWithHash(normalizedColor.value)
);

const emitColor = (next) => {
  if (!isValidHexWithHash(next)) return;
  emit('update:modelValue', next);
  emit('change', next);
  textDraft.value = next;
  touched.value = true;
};

/** PrimeVue ColorPicker hex v-model — value without # (see primevue.org/colorpicker) */
const pickerModel = computed({
  get: () => stripHash(normalizedColor.value),
  set: (value) => {
    emitColor(addHash(String(value || '')));
  },
});

const syncTextDraft = () => {
  textDraft.value = normalizedColor.value;
};

watch(
  () => props.modelValue,
  () => {
    if (!touched.value) {
      syncTextDraft();
    }
  },
  { immediate: true }
);

const commitText = () => {
  touched.value = true;
  const next = addHash(String(textDraft.value || ''));
  if (isValidHexWithHash(next)) {
    emitColor(next);
    return;
  }
  textDraft.value = normalizedColor.value;
};

const onPickerChange = () => {
  touched.value = true;
  textDraft.value = normalizedColor.value;
  emit('change', normalizedColor.value);
};

defineExpose({
  resetTouched: () => {
    touched.value = false;
    syncTextDraft();
  },
  markTouched: () => {
    touched.value = true;
  },
});
</script>

<style scoped>
.hex-color-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.hex-color-field__label {
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-color-secondary);
}

.hex-color-field__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
}

.hex-color-field__picker {
  flex-shrink: 0;
}

.hex-color-field__input {
  flex: 1;
  min-width: 0;
}

.hex-color-field__error {
  font-size: 0.75rem;
  color: #ef4444;
}
</style>
